import { useState, useEffect, useCallback } from 'react';
import { Clock, LogOut } from 'lucide-react';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';

interface SessionTimeoutModalProps {
  /** When the session expires (timestamp in milliseconds) */
  expiresAt: number;
  /** Callback when user clicks "Stay logged in" */
  onExtend: () => Promise<void>;
  /** Warning threshold in milliseconds (default: 2 minutes) */
  warningThreshold?: number;
}

export function SessionTimeoutModal({
  expiresAt,
  onExtend,
  warningThreshold = 2 * 60 * 1000, // 2 minutes
}: SessionTimeoutModalProps) {
  const { logout } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [isExtending, setIsExtending] = useState(false);

  // Calculate time remaining and show/hide modal
  useEffect(() => {
    const updateTimer = () => {
      const now = Date.now();
      const remaining = expiresAt - now;

      if (remaining <= 0) {
        // Session expired - force logout
        logout();
        return;
      }

      setSecondsRemaining(Math.ceil(remaining / 1000));

      // Show modal when within warning threshold
      if (remaining <= warningThreshold) {
        setIsVisible(true);
      }
    };

    // Initial check
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [expiresAt, warningThreshold, logout]);

  const handleExtend = useCallback(async () => {
    setIsExtending(true);
    try {
      await onExtend();
      setIsVisible(false);
    } catch (error) {
      console.error('Failed to extend session:', error);
    } finally {
      setIsExtending(false);
    }
  }, [onExtend]);

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  // Format seconds as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <div className="text-center">
          {/* Timer icon with animation */}
          <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <Clock className="h-8 w-8 text-amber-600" />
          </div>

          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Session Expiring Soon
          </h2>

          <p className="text-gray-600 mb-4">
            Your session will expire due to inactivity. Would you like to stay logged in?
          </p>

          {/* Countdown timer */}
          <div className="bg-gray-100 rounded-lg p-4 mb-6">
            <div className="text-3xl font-mono font-bold text-gray-900">
              {formatTime(secondsRemaining)}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              Time remaining
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
            <div
              className="bg-amber-500 h-2 rounded-full transition-all duration-1000"
              style={{
                width: `${Math.min(100, (secondsRemaining / (warningThreshold / 1000)) * 100)}%`,
              }}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Log Out
            </Button>
            <Button
              className="flex-1"
              onClick={handleExtend}
              isLoading={isExtending}
            >
              Stay Logged In
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
